//calendarStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { calendarService } from '../services/calendar.service';

export type PlatformName = 'youtube' | 'instagram' | 'tiktok' | 'twitter' | 'linkedin' | 'facebook' | 'blog';
export type ScheduleStatus = 'draft' | 'scheduled' | 'published' | 'failed' | 'cancelled' | 'ready' | 'overdue' | 'planning';
export type ViewMode = 'month' | 'week' | 'day';

export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export interface ContentItem {
  id: string;
  title: string;
  description: string;
  platform: PlatformName;
  status: ScheduleStatus;
  scheduledDate: Date;
  scheduledTime?: string | undefined;
  publishedAt?: Date | undefined;
  assignedTo?: string;
  assignedToName?: string;
  tags: string[];
  thumbnailUrl?: string;
  seoScore?: number;
  autoPublish: boolean;
  notes?: string;
  checklist: ChecklistItem[];
  contentType: 'video' | 'image' | 'text' | 'carousel' | 'story';
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateContentItemData {
  title: string;
  description: string;
  platform: PlatformName;
  status: ScheduleStatus;
  scheduledDate: string;
  scheduledTime?: string | undefined;
  publishedAt?: string | undefined;
  assignedTo?: string;
  assignedToName?: string;
  tags?: string[];
  thumbnailUrl?: string;
  seoScore?: number;
  autoPublish: boolean;
  notes?: string;
  checklist?: ChecklistItem[];
  contentType: 'video' | 'image' | 'text' | 'carousel' | 'story';
}

export interface UpdateContentItemData extends Partial<CreateContentItemData> {
  id: string;
}

export interface CalendarFilters {
  platforms: PlatformName[];
  statuses: ScheduleStatus[];
  assignedTo?: string;
  dateRange: 'thisWeek' | 'thisMonth' | 'nextMonth' | 'custom';
  customStartDate?: Date;
  customEndDate?: Date;
}

interface CalendarState {
  contentItems: ContentItem[];
  selectedContent: ContentItem | null;
  currentDate: Date;
  viewMode: ViewMode;
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  filters: CalendarFilters;
  showContentModal: boolean;
  showBulkModal: boolean;
  selectedItems: string[];
  sortBy: 'title' | 'scheduledDate' | 'platform' | 'status';
  sortOrder: 'asc' | 'desc';

  setCurrentDate: (date: Date) => void;
  setViewMode: (mode: ViewMode) => void;
  setSearchTerm: (term: string) => void;
  setFilters: (filters: Partial<CalendarFilters>) => void;
  setSelectedContent: (content: ContentItem | null) => void;
  setShowContentModal: (show: boolean) => void;
  setShowBulkModal: (show: boolean) => void;
  toggleItemSelection: (id: string) => void;
  clearSelection: () => void;
  setSortBy: (sort: CalendarState['sortBy']) => void;
  setSortOrder: (order: CalendarState['sortOrder']) => void;

  fetchContentItems: (startDate: Date, endDate: Date) => Promise<void>;
  createContent: (content: CreateContentItemData) => Promise<ContentItem | null>;
  updateContent: (id: string, updates: UpdateContentItemData) => Promise<ContentItem | null>;
  deleteContent: (id: string) => Promise<boolean>;
  duplicateContent: (id: string, newDate?: Date) => Promise<ContentItem | null>;
  bulkUpdate: (ids: string[], updates: Partial<ContentItem>) => Promise<boolean>;
  rescheduleContent: (id: string, newDate: Date, newTime?: string) => Promise<boolean>;
  publishContent: (id: string) => Promise<Partial<ContentItem> | null>;
  getOptimalScheduleTimes: (platform: PlatformName) => Promise<string[]>;
  exportContentItems: (format: 'csv' | 'json') => Promise<Blob>;

  getContentForDate: (date: Date) => ContentItem[];
  getContentStats: () => {
    total: number;
    byStatus: Record<ScheduleStatus, number>;
    byPlatform: Record<PlatformName, number>;
    overdue: number;
    today: number;
    thisWeek: number;
  };
}

export const useCalendarStore = create<CalendarState>()(
  devtools(
    persist(
      (set, get) => ({
        contentItems: [],
        selectedContent: null,
        currentDate: new Date(),
        viewMode: 'month',
        isLoading: false,
        error: null,
        searchTerm: '',
        filters: {
          platforms: [],
          statuses: [],
          dateRange: 'thisMonth',
        },
        showContentModal: false,
        showBulkModal: false,
        selectedItems: [],
        sortBy: 'scheduledDate',
        sortOrder: 'asc',

        setCurrentDate: (date) => {
          if (!(date instanceof Date) || isNaN(date.getTime())) {
            console.warn('Invalid date provided to setCurrentDate, using current date as fallback');
            set({ currentDate: new Date() });
            return;
          }
          set({ currentDate: date });
        },
        setViewMode: (mode) => set({ viewMode: mode }),
        setSearchTerm: (term) => set({ searchTerm: term }),
        setFilters: (newFilters) => set(state => ({
          filters: { ...state.filters, ...newFilters }
        })),
        setSelectedContent: (content) => set({ selectedContent: content }),
        setShowContentModal: (show) => set({ showContentModal: show }),
        setShowBulkModal: (show) => set({ showBulkModal: show }),
        toggleItemSelection: (id) => set(state => ({
          selectedItems: state.selectedItems.includes(id)
            ? state.selectedItems.filter(item => item !== id)
            : [...state.selectedItems, id]
        })),
        clearSelection: () => set({ selectedItems: [] }),
        setSortBy: (sort) => set({ sortBy: sort }),
        setSortOrder: (order) => set({ sortOrder: order }),

        fetchContentItems: async (startDate, endDate) => {
          set({ isLoading: true, error: null });
          try {
            const filters = {
              searchQuery: get().searchTerm,
              platform: get().filters.platforms.length === 1 ? get().filters.platforms[0] : undefined,
              status: get().filters.statuses.length === 1 ? get().filters.statuses[0] : undefined,
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
            };
            const items = await calendarService.findAll(filters);
            set({ contentItems: items, isLoading: false, error: null });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch content items';
            set({ isLoading: false, error: message });
            console.error('Failed to fetch content items:', error);
          }
        },

        createContent: async (content) => {
          set({ isLoading: true, error: null });
          try {
            const transformedContent: Partial<ContentItem> = {
              ...content,
              scheduledDate: new Date(content.scheduledDate),
              publishedAt: content.publishedAt ? new Date(content.publishedAt) : undefined,
            };
            const newContent = await calendarService.create(transformedContent);
            set(state => ({
              contentItems: [...state.contentItems, newContent],
              isLoading: false,
              error: null
            }));
            return newContent;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create content';
            set({ isLoading: false, error: message });
            console.error('Failed to create content:', error);
            return null;
          }
        },

        updateContent: async (id, updates) => {
          set({ isLoading: true, error: null });
          try {
            const transformedUpdates: Partial<ContentItem> = {
              ...updates,
              scheduledDate: updates.scheduledDate ? new Date(updates.scheduledDate) : new Date(),
              publishedAt: updates.publishedAt ? new Date(updates.publishedAt) : undefined,
            };
            const updatedContent = await calendarService.update(id, transformedUpdates);
            set(state => ({
              contentItems: state.contentItems.map(item =>
                item.id === id ? updatedContent : item
              ),
              selectedContent: state.selectedContent?.id === id ? updatedContent : state.selectedContent,
              isLoading: false,
              error: null
            }));
            return updatedContent;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update content';
            set({ isLoading: false, error: message });
            console.error('Failed to update content:', error);
            return null;
          }
        },

        deleteContent: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await calendarService.delete(id);
            set(state => ({
              contentItems: state.contentItems.filter(item => item.id !== id),
              selectedContent: state.selectedContent?.id === id ? null : state.selectedContent,
              selectedItems: state.selectedItems.filter(item => item !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to delete content';
            set({ isLoading: false, error: message });
            console.error('Failed to delete content:', error);
            return false;
          }
        },

        duplicateContent: async (id, newDate) => {
          set({ isLoading: true, error: null });
          try {
            const duplicated = await calendarService.duplicateContent(id, newDate);
            set(state => ({
              contentItems: [...state.contentItems, duplicated],
              isLoading: false,
              error: null
            }));
            return duplicated;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to duplicate content';
            set({ isLoading: false, error: message });
            console.error('Failed to duplicate content:', error);
            return null;
          }
        },

        bulkUpdate: async (ids, updates) => {
          set({ isLoading: true, error: null });
          try {
            await calendarService.bulkUpdateContent(ids, updates);
            set(state => ({
              contentItems: state.contentItems.map(item =>
                ids.includes(item.id) ? { ...item, ...updates, updatedAt: new Date() } : item
              ),
              isLoading: false,
              error: null
            }));
            get().clearSelection();
            return true;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to bulk update content';
            set({ isLoading: false, error: message });
            console.error('Failed to bulk update content:', error);
            return false;
          }
        },

      rescheduleContent: async (id, newDate, newTime) => {
          set({ isLoading: true, error: null });
          try {
            await calendarService.rescheduleContent(id, newDate, newTime);
            set(state => ({
              contentItems: state.contentItems.map(item =>
                item.id === id 
                  ? { ...item, scheduledDate: newDate, scheduledTime: newTime, updatedAt: new Date() }
                  : item
              ),
              selectedContent: state.selectedContent?.id === id 
                ? { ...state.selectedContent, scheduledDate: newDate, scheduledTime: newTime, updatedAt: new Date() }
                : state.selectedContent,
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to reschedule content';
            set({ isLoading: false, error: message });
            console.error('Failed to reschedule content:', error);
            return false;
          }
        },

        publishContent: async (id) => {
          set({ isLoading: true, error: null });
          try {
            const published = await calendarService.publishContent(id);
            set(state => ({
              contentItems: state.contentItems.map(item =>
                item.id === id ? { ...item, ...published } : item
              ),
              selectedContent: state.selectedContent?.id === id 
                ? { ...state.selectedContent, ...published } 
                : state.selectedContent,
              isLoading: false,
              error: null
            }));
            return published;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to publish content';
            set({ isLoading: false, error: message });
            console.error('Failed to publish content:', error);
            return null;
          }
        },

        getOptimalScheduleTimes: async (platform) => {
          set({ isLoading: true, error: null });
          try {
            const times = await calendarService.getOptimalScheduleTimes(platform);
            set({ isLoading: false, error: null });
            return times;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch optimal schedule times';
            set({ isLoading: false, error: message });
            console.error('Failed to fetch optimal schedule times:', error);
            return [];
          }
        },

       exportContentItems: async (format) => {
          set({ isLoading: true, error: null });
          try {
            const blob = await calendarService.exportContentItems(format);
            set({ isLoading: false, error: null });
            return blob;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to export content items';
            set({ isLoading: false, error: message });
            console.error('Failed to export content items:', error);
            throw error;
          }
        },

        getContentForDate: (date) => {
          const state = get();
          const dateStr = date.toDateString();
          return state.contentItems
            .filter(item => new Date(item.scheduledDate).toDateString() === dateStr)
            .sort((a, b) => {
              let aVal: any = a[state.sortBy];
              let bVal: any = b[state.sortBy];

              if (state.sortBy === 'scheduledDate') {
                aVal = new Date(aVal).getTime();
                bVal = new Date(bVal).getTime();
              } else if (state.sortBy === 'title' || state.sortBy === 'platform' || state.sortBy === 'status') {
                aVal = String(aVal || '').toLowerCase();
                bVal = String(bVal || '').toLowerCase();
              }

              return state.sortOrder === 'asc' 
                ? aVal < bVal ? -1 : aVal > bVal ? 1 : 0
                : aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
            });
        },

        getContentStats: () => {
          const state = get();
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);

          const stats = {
            total: state.contentItems.length,
            byStatus: {} as Record<ScheduleStatus, number>,
            byPlatform: {} as Record<PlatformName, number>,
            overdue: 0,
            today: 0,
            thisWeek: 0,
          };

          state.contentItems.forEach(item => {
            stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
            stats.byPlatform[item.platform] = (stats.byPlatform[item.platform] || 0) + 1;
            
            const itemDate = new Date(item.scheduledDate);
            
            if (itemDate.toDateString() === today.toDateString()) {
              stats.today++;
            }
            
            if (itemDate >= weekStart && itemDate <= weekEnd) {
              stats.thisWeek++;
            }
            
            if (itemDate < today && !['published', 'cancelled'].includes(item.status)) {
              stats.overdue++;
            }
          });

          return stats;
        },
      }),
      {
        name: 'calendar-storage',
        partialize: (state) => ({
          contentItems: state.contentItems.map(item => ({
            ...item,
            scheduledDate: item.scheduledDate.toISOString(),
            publishedAt: item.publishedAt ? item.publishedAt.toISOString() : undefined,
            createdAt: item.createdAt.toISOString(),
            updatedAt: item.updatedAt.toISOString(),
          })),
          currentDate: state.currentDate.toISOString(),
          viewMode: state.viewMode,
          searchTerm: state.searchTerm,
          filters: {
            ...state.filters,
            customStartDate: state.filters.customStartDate ? state.filters.customStartDate.toISOString() : undefined,
            customEndDate: state.filters.customEndDate ? state.filters.customEndDate.toISOString() : undefined,
          },
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }),
        merge: (persistedState: any, currentState) => {
          const rehydratedDate = persistedState.currentDate ? new Date(persistedState.currentDate) : new Date();
          return {
            ...currentState,
            contentItems: persistedState.contentItems.map((item: any) => ({
              ...item,
              scheduledDate: new Date(item.scheduledDate),
              publishedAt: item.publishedAt ? new Date(item.publishedAt) : undefined,
              createdAt: new Date(item.createdAt),
              updatedAt: new Date(item.updatedAt),
            })),
            currentDate: rehydratedDate instanceof Date && !isNaN(rehydratedDate.getTime()) ? rehydratedDate : new Date(),
            viewMode: persistedState.viewMode,
            searchTerm: persistedState.searchTerm,
            filters: {
              ...persistedState.filters,
              customStartDate: persistedState.filters.customStartDate ? new Date(persistedState.filters.customStartDate) : undefined,
              customEndDate: persistedState.filters.customEndDate ? new Date(persistedState.filters.customEndDate) : undefined,
            },
            sortBy: persistedState.sortBy,
            sortOrder: persistedState.sortOrder,
          };
        },
      }
    ),
    {
      name: 'calendar-store',
    }
  )
);